import { useEffect, useRef } from "react";
import { usePage, type SectionProps } from "@/flash/context";
import { arr, cn, rv, str, strs } from "@/flash/ui";
import { GhostLink, Kicker, MaskTitle, Pic, Shell, pad2 } from "./kit";

/* 04 · Capability chapters — desktop: section-local sticky editorial sequence driven by native scroll (no pin, no ScrollTrigger).
   Mobile/tablet: three plain vertical chapters. */

const SLOTS = ["showcase", "showcase2", "showcase3"];
const FOCUS = ["object-center", "object-[center_35%]", "object-[center_60%]"];

const clamp = (v: number) => Math.min(1, Math.max(0, v));
const ease = (a: number, b: number, v: number) => {
  const t = clamp((v - a) / (b - a));
  return t * t * (3 - 2 * t);
};

function Points({ list }: { list: string[] }) {
  if (!list.length) return null;
  return (
    <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-foreground/75">
      {list.slice(0, 3).map((p) => (
        <li key={p} className="flex items-center gap-2 whitespace-nowrap">
          <span className="size-1 bg-primary" aria-hidden />
          {p}
        </li>
      ))}
    </ul>
  );
}

function Title({ t }: { t: string }) {
  return (
    <>
      <span className="text-primary">{t.slice(0, 1)}</span>
      {t.slice(1)}
    </>
  );
}

export function SysChapters({ title, subtitle, items }: SectionProps) {
  const { images, ctaHref } = usePage();
  const list = arr(items).slice(0, 3);
  const trackRef = useRef<HTMLDivElement>(null);
  const imgRefs = useRef<(HTMLDivElement | null)[]>([]);
  const txtRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tickRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const barRef = useRef<HTMLSpanElement>(null);
  const dimRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const mq = window.matchMedia("(min-width: 1024px)");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    let cur = -1;
    const target = () => {
      const r = track.getBoundingClientRect();
      const span = r.height - window.innerHeight;
      return span > 0 ? clamp(-r.top / span) : 0;
    };
    const apply = (p: number) => {
      // 每章都有较长的停留区，切换只发生在两段较宽的过渡带里
      const t1 = ease(0.24, 0.4, p);
      const t2 = ease(0.6, 0.76, p);
      const [a, b, c] = imgRefs.current;
      const [da, db] = dimRefs.current;
      if (a) a.style.transform = `scale3d(${1 - 0.05 * t1},${1 - 0.05 * t1},1)`;
      if (da) da.style.opacity = String(0.55 * t1);
      if (b) {
        const s = (1.08 - 0.08 * t1) * (1 - 0.04 * t2);
        b.style.clipPath = `inset(${(1 - t1) * 100}% 0 0 0)`;
        b.style.transform = `scale3d(${s},${s},1)`;
      }
      if (db) db.style.opacity = String(0.5 * t2);
      if (c) {
        const s = 1.06 - 0.06 * t2;
        c.style.clipPath = `inset(0 0 0 ${(1 - t2) * 100}%)`;
        c.style.transform = `scale3d(${s},${s},1)`;
      }
      const w = [1 - t1, t1 * (1 - t2), t2];
      txtRefs.current.forEach((el, i) => {
        if (!el) return;
        const o = w[i];
        el.style.opacity = String(o);
        el.style.transform = `translate3d(0,${(1 - o) * (i === 0 || (i === 1 && t2 > 0) ? -24 : 24)}px,0)`;
        el.style.pointerEvents = o > 0.5 ? "auto" : "none";
        el.setAttribute("aria-hidden", o > 0.5 ? "false" : "true");
      });
      const active = t2 > 0.5 ? 2 : t1 > 0.5 ? 1 : 0;
      tickRefs.current.forEach((el, i) => {
        if (el) el.dataset.on = i === active ? "1" : "0";
      });
      if (barRef.current) barRef.current.style.transform = `scale3d(1,${p},1)`;
    };
    // 鼠标滚轮是一格一格跳的：这里让画面进度每帧向真实滚动位置缓动靠拢，消除顿挫感（页面滚动本身仍是原生的）
    const tick = () => {
      raf = 0;
      if (!mq.matches) return;
      const t = target();
      if (cur < 0 || reduce) cur = t;
      else cur += (t - cur) * 0.07;
      if (Math.abs(t - cur) < 0.0005) cur = t;
      apply(cur);
      if (cur !== t) raf = requestAnimationFrame(tick);
    };
    const req = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };
    tick();
    window.addEventListener("scroll", req, { passive: true });
    window.addEventListener("resize", req);
    mq.addEventListener?.("change", req);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", req);
      window.removeEventListener("resize", req);
      mq.removeEventListener?.("change", req);
    };
  }, [list.length]);

  return (
    <section id="showcase" className="sx-env relative py-24 md:py-36 lg:pb-0">
      <Shell>
        <div className="grid gap-6 lg:grid-cols-12 lg:gap-8">
          <Kicker className="lg:col-span-4">Capabilities</Kicker>
          <div className="flex flex-col gap-4 lg:col-span-8">
            <MaskTitle className="sx-display-md">{str(title)}</MaskTitle>
            {subtitle ? <p {...rv(1)} className="max-w-lg text-[15px] leading-relaxed text-muted-foreground">{str(subtitle)}</p> : null}
          </div>
        </div>

        {/* Mobile / tablet: native vertical chapters */}
        <div className="mt-16 flex flex-col md:mt-24 lg:hidden">
          {list.map((it, i) => (
            <article key={i} className="flex flex-col gap-8 border-t border-white/[.08] py-12 md:py-16">
              <div className="flex flex-col">
                <span className="text-[13px] tabular-nums text-muted-foreground">{pad2(i + 1)} / {pad2(list.length)}</span>
                <MaskTitle as="h3" className="sx-display-lg mt-5">
                  <Title t={str(it.title)} />
                </MaskTitle>
                <p {...rv(1)} className="mt-6 max-w-md text-[15px] leading-relaxed text-muted-foreground">{str(it.description)}</p>
                <Points list={strs(it.points)} />
                <GhostLink to={ctaHref} className="mt-8 w-fit">Request a demo</GhostLink>
              </div>
              <Pic img={images[SLOTS[i]]} className="aspect-[16/10] w-full rounded-[2px]" imgClass={FOCUS[i]} sizes="100vw" />
            </article>
          ))}
        </div>
      </Shell>

      {/* Desktop: sticky editorial stage inside a ~260vh local track */}
      <div ref={trackRef} className="relative mt-24 hidden h-[420vh] lg:block">
        <div className="sticky top-0 flex h-screen items-center">
          <Shell className="grid grid-cols-12 items-center gap-8">
            <div className="relative col-span-4 flex h-[min(560px,70vh)] gap-6">
              <div className="relative w-px shrink-0 bg-white/10" aria-hidden>
                <span ref={barRef} className="absolute inset-0 origin-top bg-primary" style={{ transform: "scaleY(0)" }} />
              </div>
              <div className="relative flex-1">
                <div className="flex gap-4 text-[13px] tabular-nums">
                  {list.map((_, i) => (
                    <span
                      key={i}
                      ref={(el) => {
                        tickRefs.current[i] = el;
                      }}
                      data-on={i === 0 ? "1" : "0"}
                      className="text-muted-foreground transition-colors duration-300 data-[on='1']:text-foreground"
                    >
                      {pad2(i + 1)}
                    </span>
                  ))}
                </div>
                {list.map((it, i) => (
                  <div
                    key={i}
                    ref={(el) => {
                      txtRefs.current[i] = el;
                    }}
                    className="absolute inset-x-0 top-14 flex flex-col will-change-transform"
                    style={{ opacity: i === 0 ? 1 : 0 }}
                  >
                    <h3 className="sx-display-lg">
                      <Title t={str(it.title)} />
                    </h3>
                    <p className="mt-6 max-w-xs text-[15px] leading-relaxed text-muted-foreground">{str(it.description)}</p>
                    <Points list={strs(it.points)} />
                    <GhostLink to={ctaHref} className="mt-8 w-fit">Request a demo</GhostLink>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative col-span-8 aspect-[16/10] w-full overflow-hidden rounded-[2px] bg-black">
              {list.map((_, i) => (
                <div
                  key={i}
                  ref={(el) => {
                    imgRefs.current[i] = el;
                  }}
                  className={cn("absolute inset-0 will-change-transform")}
                  style={i === 0 ? undefined : { clipPath: i === 1 ? "inset(100% 0 0 0)" : "inset(0 0 0 100%)" }}
                >
                  <Pic img={images[SLOTS[i]]} reveal={false} className="absolute inset-0" imgClass={FOCUS[i]} sizes="62vw" />
                  {i < 2 ? (
                    <div
                      ref={(el) => {
                        dimRefs.current[i] = el;
                      }}
                      className="pointer-events-none absolute inset-0 bg-black"
                      style={{ opacity: 0 }}
                      aria-hidden
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </Shell>
        </div>
      </div>
    </section>
  );
}